import React from "react";
import { motion } from "framer-motion";
import { GraduationCap, Award, Users } from "lucide-react";
import { SITE } from "@/data/site";

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-[#08080A] py-24 lg:py-32 overflow-hidden"
    >
      <div
        className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-[#F5A623]/[0.04] blur-[140px]"
        aria-hidden
      />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-start">
        <div className="lg:col-span-5 relative">
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-24 h-24 border-l border-t border-[#F5A623]/70" />
            <div className="absolute -bottom-4 -right-4 w-24 h-24 border-r border-b border-[#F5A623]/70" />
            <img
              src={SITE.founder.photo}
              alt={SITE.founder.name}
              className="relative w-full h-[460px] lg:h-[560px] object-cover object-top rounded-md"
              data-testid="about-founder-photo"
            />
            <div className="absolute inset-0 rounded-md bg-gradient-to-t from-[#08080A] via-transparent to-transparent pointer-events-none" />
          </div>
          <div className="mt-6 glass-card rounded-md p-5 max-w-sm relative lg:-mt-16 lg:ml-12 backdrop-blur-md">
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#F5A623] font-semibold">
              Founder
            </p>
            <h3 className="font-serif text-2xl silver-text mt-2">{SITE.founder.name}</h3>
            <p className="text-sm text-[#B7BBC4] mt-1">{SITE.founder.title}</p>
          </div>
        </div>

        <div className="lg:col-span-7">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#F5A623] font-semibold"
          >
            <span className="divider-gold" /> About Us
          </motion.span>

          <motion.h2
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight silver-text"
            data-testid="about-headline"
          >
            We sit at the centre of your financial life — so the picture stays whole.
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-6 text-base lg:text-lg text-[#B7BBC4] leading-relaxed"
            data-testid="about-bio"
          >
            {SITE.founder.bio}
          </motion.p>

          <div className="mt-10 grid sm:grid-cols-3 gap-6">
            {[
              { icon: GraduationCap, t: "Education-First", d: "We teach before we recommend." },
              { icon: Users, t: "Family-Centred", d: "Built for emerging affluent families." },
              { icon: Award, t: "Tax & Insurance Expert", d: "Specialised, multi-domain expertise." },
            ].map(({ icon: Icon, t, d }) => (
              <motion.div
                key={t}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="glass-card rounded-md p-5 hover:-translate-y-1 transition-all duration-300"
                data-testid={`about-feature-${t.toLowerCase().replace(/\W+/g, "-")}`}
              >
                <Icon className="w-5 h-5 text-[#F5A623]" strokeWidth={1.5} />
                <h4 className="mt-3 font-serif text-lg text-white">{t}</h4>
                <p className="mt-1 text-sm text-[#9CA0AA]">{d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
